/**
 * BraintreeDropIn Component
 * 
 * React wrapper for Braintree Drop-in UI.
 * Manages Drop-in lifecycle, initialization, and nonce extraction.
 * 
 * Task: T055
 * 
 * Props:
 * - clientToken: Braintree client token (required)
 * - onPaymentMethodReceived: Callback when payment method nonce is received
 * - onError: Callback when error occurs
 * - containerRef: Optional ref to container element
 */

import React, { useEffect, useRef, useState } from 'react';
import { Card, CardBody, Button, Typography } from '@material-tailwind/react';
import {
  createDropinInstance,
  requestPaymentMethod,
  teardownDropin,
  isValidClientToken,
  setupDropinEventHandlers,
} from '../services/braintreeClient';
import LoadingSpinner from './LoadingSpinner';
import ErrorMessage from './ErrorMessage';

const BraintreeDropIn = ({ 
  clientToken, 
  onPaymentMethodReceived, 
  onError,
  containerRef,
  buttonText = 'Save Payment Method',
  title = 'Enter Payment Method'
}) => {
  const dropinContainerRef = useRef(null);
  const dropinInstanceRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPaymentRequestable, setIsPaymentRequestable] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [formInteractionStarted, setFormInteractionStarted] = useState(false);

  // Initialize Drop-in UI on mount
  useEffect(() => {
    let isMounted = true;

    const initializeDropin = async () => {
      // Validate client token
      if (!clientToken) {
        setError('No client token provided');
        setIsLoading(false);
        return;
      }

      if (!isValidClientToken(clientToken)) {
        setError('Invalid client token format');
        setIsLoading(false);
        onError?.(new Error('Invalid client token format'));
        return;
      }

      // Clear container before initializing
      const container = containerRef?.current || dropinContainerRef.current;
      if (container) {
        container.innerHTML = '';
      }

      try {
        setIsLoading(true);
        setError(null);

        // Create Drop-in instance
        const instance = await createDropinInstance({
          authorization: clientToken,
          container: container,
        });

        if (!isMounted) {
          await teardownDropin(instance);
          return;
        }

        dropinInstanceRef.current = instance;

        // Set up event handlers
        setupDropinEventHandlers(instance, {
          onPaymentMethodRequestable: () => {
            if (isMounted) {
              setIsPaymentRequestable(true);
              // T092: Track that user has interacted with payment form
              setFormInteractionStarted(true);
            }
          },
          onNoPaymentMethodRequestable: () => {
            if (isMounted) {
              setIsPaymentRequestable(false);
            }
          },
        });

        setIsLoading(false);
      } catch (err) {
        console.error('[BraintreeDropIn] Initialization error:', err);
        if (isMounted) {
          // T084: Enhanced error message for Drop-in UI load failure
          let errorMessage = 'Failed to load payment form. Please refresh the page and try again.';
          
          if (err.message && err.message.includes('network')) {
            errorMessage = 'Network error: Unable to load payment form. Please check your connection and try again.';
          } else if (err.message && err.message.includes('timeout')) {
            errorMessage = 'Connection timeout: Please check your internet connection and try again.';
          }
          
          setError(errorMessage);
          setIsLoading(false);
          onError?.(err);
        }
      }
    };

    initializeDropin();

    // Cleanup on unmount
    return () => {
      isMounted = false;
      
      // T092: Log abandoned payment form (analytics only - no data storage)
      if (formInteractionStarted && dropinInstanceRef.current) {
        console.log('[Analytics] Payment form abandoned', {
          timestamp: new Date().toISOString(),
          // No PII or payment data logged
        });
      }
      
      if (dropinInstanceRef.current) {
        teardownDropin(dropinInstanceRef.current);
        dropinInstanceRef.current = null;
      }
    };
  }, [clientToken, containerRef, onError, retryCount]); // T084: Add retryCount to deps for retry functionality

  // T084: Retry handler for Drop-in UI load failures
  const handleRetry = () => {
    setError(null);
    setIsLoading(true);
    setRetryCount(prev => prev + 1);
  };

  // Handle payment submission
  const handleSubmit = async () => {
    if (!dropinInstanceRef.current) {
      const error = new Error('Drop-in UI not initialized');
      setError(error.message);
      onError?.(error);
      return;
    }

    try {
      setIsProcessing(true);
      setError(null);

      // Request payment method nonce
      const payload = await requestPaymentMethod(dropinInstanceRef.current);

      if (!payload.nonce) {
        throw new Error('No payment method nonce received');
      }

      // Pass nonce to parent component
      onPaymentMethodReceived?.(payload);
    } catch (err) {
      console.error('[BraintreeDropIn] Submit error:', err);
      setError(err.message || 'Failed to process payment information');
      setIsProcessing(false);
      onError?.(err);
    }
  };

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardBody>
        {title && (
          <Typography variant="h5" color="blue-gray" className="mb-4">
            {title}
          </Typography>
        )}

        {error && (
          <>
            <ErrorMessage 
              message={error} 
              dismissible 
              onDismiss={() => setError(null)} 
            />
            {/* T084: Retry button for Drop-in UI load failures */}
            <Button
              variant="outlined"
              size="md"
              className="w-full mt-4 border-fotl-purple text-fotl-purple hover:bg-fotl-purple hover:text-white"
              onClick={handleRetry}
            >
              Retry
            </Button>
          </>
        )}

        {isLoading && (
          <LoadingSpinner 
            size="lg" 
            message="Loading payment form..." 
          />
        )}

        {/* Drop-in container */}
        <div
          ref={dropinContainerRef}
          className={isLoading ? 'hidden' : 'mb-4'}
        />

        {/* Submit button */}
        {!isLoading && (
          <Button
            size="lg"
            className="w-full bg-fotl-purple hover:bg-fotl-purple-dark"
            onClick={handleSubmit}
            disabled={!isPaymentRequestable || isProcessing}
          >
            {isProcessing ? 'Processing...' : buttonText}
          </Button>
        )}

        <Typography
          variant="small"
          color="blue-gray"
          className="mt-4 text-center opacity-70"
        >
          Your payment information is securely tokenized and vaulted by Braintree.
          We never store your card details.
        </Typography>
      </CardBody>
    </Card>
  );
};

export default BraintreeDropIn;
